import { useEffect, useId, useRef, type ReactNode } from 'react';
import styles from './Overlay.module.css';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** "Limpar" — resets the sheet's own state without closing it. */
  onReset?: () => void;
  resetLabel?: string;
  children: ReactNode;
  /**
   * Sticky footer. The canvas has the confirm button state the resulting count
   * ("Ver 12 voltas") so the user knows what they will find before the sheet closes.
   */
  footer?: ReactNode;
}

export function Sheet({
  open,
  onClose,
  title,
  onReset,
  resetLabel = 'Limpar',
  children,
  footer,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Escape closes; the page behind must not scroll while the sheet is up.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.sheetScrim}
      // Dismiss on scrim, but not when the click started inside the panel.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className={styles.grabber} aria-hidden="true" />
        <div className={styles.sheetHead}>
          <h2 className={styles.sheetTitle} id={titleId}>
            {title}
          </h2>
          {onReset && (
            <button type="button" className={styles.sheetReset} onClick={onReset}>
              {resetLabel}
            </button>
          )}
        </div>
        {children}
        {footer != null && <div className={styles.sheetFooter}>{footer}</div>}
      </div>
    </div>
  );
}

export function SheetGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.sheetGroup}>
      <div className={styles.sheetLabel}>{label}</div>
      {children}
    </div>
  );
}
